import * as types from './types';

const defaultComment = {
  content: '',
};

const defaultPostUpload = {
  isLoading: false,
  progress: 0,
};

const defaultPostFilter = {
  search: '',
  moderationState: [],
  tag: null,
};

const updateItemOnList = (list, newItem) => list.map((item) => {
  if (item.id === newItem.id) {
    return { ...item, ...newItem };
  }
  return item;
});

export const state = {
  communitiesList: [],
  activeCommunity: {},
  activeCommunityUser: {},
  activeUser: {},
  asidePosts: [],
  posts: [],
  tags: [],
  totalPosts: 0,
  totalAsidePosts: 0,
  activePost: {},
  postComments: [],
  postTotalComments: 0,
  activeComment: { ...defaultComment },
  commentReplies: {},
  newCommentReplies: {},
  activeCommunityTerms: {},
  popularTags: [],
  postFilter: { ...defaultPostFilter },
  postsOrderBy: 'created_at',
  postActiveUpload: { ...defaultPostUpload },
  comments: [],
  totalComments: 0,
  mobileSidebar: {
    rules: false,
    tags: false,
    search: false,
  },
  users: [],
  totalUsers: 0,
  userFilter: '',
  usersOrderBy: 'qty_valid_posts',
  activeUserNotifications: [],
  totalNotifications: 0,
  communityUserProfile: {},
  badges: [],
  replyingComment: {},
};

export const mutations = {
  [types.SET_COMMUNITIES_LIST](state, communitiesList) {
    state.communitiesList = communitiesList;
  },
  [types.SET_ACTIVE_COMMUNITY](state, community) {
    state.activeCommunity = community;
  },
  [types.SET_ACTIVE_COMMUNITY_USER](state, communityUser) {
    const { user, ...communityUserData } = communityUser;

    state.activeUser = { ...communityUserData, ...user };
    state.activeCommunityUser = communityUser;
  },
  [types.SET_TAGS](state, tags) {
    state.tags = tags;
  },
  [types.SET_ASIDE_POSTS](state, asidePosts) {
    state.asidePosts = asidePosts;
  },
  [types.SET_ASIDE_POSTS_COUNTER](state, totalAsidePosts) {
    state.totalAsidePosts = totalAsidePosts;
  },
  [types.APPEND_ASIDE_POST](state, postItem) {
    state.asidePosts = [postItem, ...state.asidePosts];
  },
  [types.SET_POSTS](state, posts) {
    state.posts = posts;
  },
  [types.APPEND_POSTS](state, posts) {
    state.posts = [...state.posts].concat(posts);
  },
  [types.UPDATE_POST_LIST](state, post) {
    const posts = state.posts.map((postItem) => {
      if (postItem.id === post.id) {
        return Object.assign({}, postItem, post);
      }

      return postItem;
    });

    state.posts = [...posts];
  },
  [types.SET_POSTS_COUNTER](state, totalPosts) {
    state.totalPosts = totalPosts;
  },
  [types.SET_ACTIVE_POST](state, post) {
    state.activePost = post;
  },
  [types.ADD_COMMENT_TO_LIST](state, comment) {
    state.postComments.unshift(comment);
  },
  [types.APPEND_POST_COMMENTS](state, comments) {
    state.postComments = [...state.postComments].concat(comments);
  },
  [types.SET_POST_COMMENTS](state, comments) {
    state.postComments = comments;
  },
  [types.SET_POST_COMMENTS_COUNTER](state, commentsCounter) {
    state.postTotalComments = commentsCounter;
  },
  [types.SET_ACTIVE_COMMENT](state, comment) {
    state.activeComment = { ...comment };
  },
  [types.SET_REPLIES_TO_COMMENT](state, { comment, replies, reset }) {
    const repliesOfComments = state.commentReplies[comment.id] || [];
    const commentReplies = state.commentReplies;

    commentReplies[comment.id] = reset ? [...replies] : [...repliesOfComments, ...replies];

    state.commentReplies = { ...commentReplies };
  },
  [types.RESET_COMMENT_REPLIES](state) {
    state.commentReplies = {};
  },
  [types.RESET_ACTIVE_COMMENT](state) {
    state.activeComment = { ...defaultComment };
  },
  [types.SET_ACTIVE_COMMUNITY_TERMS](state, communityTerms) {
    state.activeCommunityTerms = communityTerms;
  },
  [types.UPDATE_POST_ON_ASIDE_LIST](state, postItem) {
    state.asidePosts = updateItemOnList(state.asidePosts, postItem);
  },
  [types.UPDATE_POST_ON_LIST](state, post) {
    state.posts = updateItemOnList(state.posts, post);
  },
  [types.UPDATE_POST_COMMENT_ON_LIST](state, comment) {
    state.postComments = updateItemOnList(state.postComments, comment);
  },
  [types.UPDATE_REPLY_ON_LIST](state, comment) {
    const id = comment.comment_id;
    const onNewList = state.newCommentReplies[id] ?
      state.newCommentReplies[id].some(reply => reply.id === comment.id) :
      false;

    const replyList = onNewList ? 'newCommentReplies' : 'commentReplies';
    const commentReplies = state[replyList];
    commentReplies[id] = updateItemOnList(state[replyList][id], comment);
    state[replyList] = { ...commentReplies };
  },
  [types.ADD_REPLY_ON_LIST](state, reply) {
    const newCommentReplies = state.newCommentReplies;
    const id = reply.comment_id;
    const newRepliesOfComment = state.newCommentReplies[id] || [];

    newCommentReplies[id] = [...newRepliesOfComment, reply];

    state.newCommentReplies = { ...newCommentReplies };
  },
  [types.SET_POPULAR_TAGS](state, popularTags) {
    state.popularTags = popularTags;
  },
  [types.SET_TAG_FILTER](state, tagFilter) {
    state.postFilter = {
      ...state.postFilter,
      tag: tagFilter,
    };
  },
  [types.SET_SEARCH_FILTER](state, searchFilter) {
    state.postFilter = {
      ...state.postFilter,
      search: searchFilter,
    };
  },
  [types.SET_USER_SEARCH](state, search) {
    state.userFilter = search;
  },
  [types.SET_MODERATION_STATE_FILTER](state, filters) {
    filters = Array.isArray(filters) ? filters : [filters];

    state.postFilter = {
      ...state.postFilter,
      moderationState: filters,
    };
  },
  [types.TOGGLE_MODERATION_STATE_FILTER](state, moderationStateFilter) {
    const moderationStates = state.postFilter.moderationState;
    const stateIndex = moderationStates.indexOf(moderationStateFilter);

    if (stateIndex !== -1) {
      moderationStates.splice(stateIndex, 1);
    } else {
      moderationStates.push(moderationStateFilter);
    }

    state.postFilter = {
      ...state.postFilter,
      moderationState: moderationStates,
    };
  },
  [types.RESET_POST_FILTER](state) {
    state.postFilter = { ...defaultPostFilter };
  },
  [types.SET_POSTS_ORDER_BY](state, order) {
    state.postsOrderBy = order;
  },
  [types.SET_POST_UPLOAD](state, progress) {
    state.postActiveUpload.isLoading = true;
    state.postActiveUpload.progress = progress;
  },
  [types.RESET_POST_UPLOAD](state) {
    state.postActiveUpload = { ...defaultPostUpload };
  },
  [types.SET_COMMENTS](state, comments) {
    state.comments = comments;
  },
  [types.SET_TOTAL_COMMENTS](state, totalComments) {
    state.totalComments = totalComments;
  },
  [types.UPDATE_COMMENT_ON_LIST](state, comment) {
    const comments = state.comments;

    comments.map((item) => {
      if (item.id === comment.id) {
        item.moderation_state = comment.moderation_state;
      }
      return item;
    });

    state.comments = [...comments];
  },
  [types.CHANGE_MOBILE_SIDEBAR](state, data) {
    const { key, value } = data;

    state.mobileSidebar = { ...state.mobileSidebar, [key]: value };
  },
  [types.SET_USERS](state, users) {
    state.users = users;
  },
  [types.SET_USERS_COUNTER](state, totalUsers) {
    state.totalUsers = totalUsers;
  },
  [types.SET_USERS_ORDER_BY](state, order) {
    state.usersOrderBy = order;
  },
  [types.UPDATE_USER_STATE_ON_LIST](state, userState) {
    const userList = state.users.map((user) => {
      const userId = Number(user.id);

      if (userId === userState.id) {
        user.permission = userState.permission;
        user.is_active = userState.is_active;
        user.is_muted = userState.is_muted;
      }
      return user;
    });

    state.users = [...userList];
  },
  [types.UPDATE_READ_NOTIFICATION](state, notifications) {
    const newActiveNotifications = state.activeUserNotifications.map((item) => {
      const newNotification = notifications
        .find(notification => item.id.includes(notification.id[0]));

      if (!newNotification) {
        return item;
      }

      return { ...item, read: newNotification.read };
    });

    state.activeUserNotifications = [...newActiveNotifications];
  },
  [types.MARK_ALL_AS_READ_NOTIFICATION](state) {
    const notifications = state.activeUserNotifications.map(item => ({ ...item, read: true }));
    state.activeUserNotifications = [...notifications];
  },
  [types.SET_USER_NOTIFICATIONS](state, { notifications, page }) {
    state.activeUserNotifications =
      page === 1 ?
      notifications :
      [...state.activeUserNotifications, ...notifications];
  },
  [types.SET_TOTAL_USER_NOTIFICATIONS](state, totalNotifications) {
    state.totalNotifications = totalNotifications;
  },
  [types.UPDATE_USER_EMAIL_SETTINGS_ON_LIST](state, communityUser) {
    const userList = state.users.map((user) => {
      const userId = user.id;

      if (userId === communityUser.id) {
        user.email_settings = communityUser.email_settings;
      }
      return user;
    });

    state.users = [...userList];
  },
  [types.SET_COMMUNITY_USER_PROFILE](state, communityUser) {
    state.communityUserProfile = communityUser;
  },
  [types.RESET_COMMUNITY_USER_PROFILE](state) {
    state.communityUserProfile = {};
  },
  [types.SET_BADGES](state, badges) {
    state.badges = badges;
  },
  [types.UPDATE_USER_BADGES_ON_LIST](state, { id, badges }) {
    const userList = state.users.map((user) => {
      if (user.id === id) {
        user.badges = badges;
        user.qty_badges = badges.length;
      }
      return user;
    });

    state.users = [...userList];
  },
  [types.SET_REPLYING_COMMENT](state, comment) {
    state.replyingComment = comment;
  },
};



// WEBPACK FOOTER //
// ./src/screens/Community/store/mutations.js